import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);
  const [mobileGuidesOpen, setMobileGuidesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const navLinkClasses = (isActive = false) =>
    `block px-4 py-3 rounded-xl text-sm font-medium transition-colors cursor-pointer ${
      isActive
        ? 'bg-primary-50 text-primary-700'
        : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
    }`;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className={`font-heading text-xl md:text-2xl font-bold tracking-wide whitespace-nowrap cursor-pointer transition-colors duration-300 ${
              scrolled ? 'text-foreground-900' : 'text-white'
            }`}
          >
            SuperBuySheet
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-3 md:gap-4">
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                onBlur={() => setTimeout(() => setDropdownOpen(false), 200)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-300 border ${
                  scrolled
                    ? 'bg-background-100 border-background-300 text-foreground-800 hover:bg-background-200'
                    : 'bg-white/15 border-white/30 text-white hover:bg-white/25'
                }`}
              >
                <i className="ri-stack-line"></i>
                <span>Shop Categories</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-56 bg-background-50 rounded-xl border border-background-200/80 py-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 cursor-pointer transition-colors duration-150"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={cat.icon}></i>
                      </span>
                      <span className="whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 border ${
                scrolled
                  ? 'border-primary-500 text-primary-600 hover:bg-primary-50'
                  : 'border-white/60 text-white hover:bg-white/15'
              }`}
            >
              Superbuy Sheet
              <i className="ri-external-link-line"></i>
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-xl transition-colors cursor-pointer ${
              scrolled ? 'text-foreground-800 hover:bg-background-100' : 'text-white hover:bg-white/15'
            }`}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <i className="ri-close-line text-xl"></i>
            ) : (
              <i className="ri-menu-3-line text-xl"></i>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="absolute top-0 right-0 w-full max-w-sm h-full bg-background-50 shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
              <span className="font-heading text-lg font-bold text-foreground-900">Menu</span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="p-4 space-y-2">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className={navLinkClasses(true)}>
                <span className="flex items-center gap-3">
                  <i className="ri-home-line text-lg text-primary-500"></i>
                  Home
                </span>
              </Link>

              {/* Mobile Categories accordion */}
              <div>
                <button
                  onClick={() => setMobileCatsOpen(!mobileCatsOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="flex items-center gap-3">
                    <i className="ri-stack-line text-lg text-accent-500"></i>
                    Shop Categories
                  </span>
                  <i className={`ri-arrow-down-s-line transition-transform ${mobileCatsOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileCatsOpen && (
                  <div className="mt-1 ml-4 pl-4 border-l-2 border-background-200 space-y-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-700 cursor-pointer transition-colors"
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={cat.icon}></i>
                        </span>
                        <span className="whitespace-nowrap">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Guides accordion */}
              <div>
                <button
                  onClick={() => setMobileGuidesOpen(!mobileGuidesOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="flex items-center gap-3">
                    <i className="ri-article-line text-lg text-secondary-500"></i>
                    Guides & Articles
                  </span>
                  <i className={`ri-arrow-down-s-line transition-transform ${mobileGuidesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileGuidesOpen && (
                  <div className="mt-1 ml-4 pl-4 border-l-2 border-background-200 space-y-1">
                    {articles.slice(0, 6).map((art) => (
                      <Link
                        key={art.slug}
                        to={`/article/${art.slug}`}
                        onClick={() => setMobileMenuOpen(false)}
                        className="block px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-700 cursor-pointer transition-colors"
                      >
                        {art.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <div className="pt-3 border-t border-background-200">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-primary-500 text-white text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-external-link-line text-lg"></i>
                  Superbuy Sheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 mt-2 rounded-xl border border-primary-300 text-primary-700 text-sm font-semibold cursor-pointer hover:bg-primary-50 transition-colors"
                >
                  <i className="ri-shopping-bag-line text-lg"></i>
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}